import { Drawer, Grid, Image, Stack, Tabs, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconFilter } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useErrorMessages } from '../../constants/content'
import useSessionStorage from '../../hooks/useSessionStorage'
import {
  Entry,
  HELP_CENTER_CHUNKED_CONTENT_TYPE,
  HELP_CENTER_RESOURCES_CONTENT_TYPE,
} from '../../plugins/contentful/api'
import client from '../../plugins/contentful/client'
import { BodyStack } from '../../styled/components'
import { getKeyValue } from '../../utils/utils'
import RootButton from '../Buttons/RootButton'
import ParseRichText from '../Contentful/ParseRichText'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'
import classes from './HelpCenter.module.css'

interface ListIconProps {
  visible: boolean
}

const ListIcon: React.FC<ListIconProps> = ({ visible }) => (
  <div style={{ visibility: visible ? 'visible' : 'hidden' }}>&#9632;</div>
)

interface RowItemsTabProps {
  image: string
  title: string
  description: JSX.Element
  button: string
  link: string
}

const RowItemsTab: React.FC<RowItemsTabProps> = ({ image, title, description, button, link }) => (
  <Stack
    style={{
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
    }}
  >
    <Stack style={{ flexDirection: 'column', gap: '14px' }}>
      <Image src={image} />
      <Title order={4} className={classes.header}>
        {title}
      </Title>
      <Text className={classes.paragraph} style={{ p: { margin: 0 } }}>
        {description}
      </Text>
    </Stack>
    <RootButton secondary onClick={() => window.open(link, '_blank')}>
      {button}
    </RootButton>
  </Stack>
)

const Component: React.FC = () => {
  const theme = useMantineTheme()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const errorMessages = useErrorMessages()
  const navigate = useNavigate()
  const { tabId = 'start' } = useParams()
  useEffect(() => {
    if (!window.location.pathname.endsWith(`/${tabId}`)) {
      navigate(`${tabId}`, { replace: true, relative: 'route' })
    }
  }, [tabId, window.location.pathname, navigate])
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const [drawerOpened, { open, close }] = useDisclosure(false)

  // Content (Help Center)
  const [helpCenterContent, setHelpCenterContent] = useSessionStorage<Entry[]>(
    `helpCenterContent-${language}`,
    []
  )
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  // Last Row Tab Content (Additional Resources)
  const [lastRowTabContent, setLastRowTabContent] = useSessionStorage<Entry[]>(
    `helpCenterLastRow-${language}`,
    []
  )
  const [isLoadingLastRow, setIsLoadingLastRow] = useState<boolean>(false)
  const [isErrorLastRow, setIsErrorLastRow] = useState<boolean>(false)

  const changeActiveTab = useCallback(
    (tab: string | null) => {
      if (tab === null) return
      navigate(`/helpcenter/${tab}`)
    },
    [navigate]
  )

  useEffect(() => {
    if (!helpCenterContent.length) {
      setIsLoadingContent(true)
      client
        .getEntries({
          content_type: HELP_CENTER_CHUNKED_CONTENT_TYPE,
          locale: language,
        })
        .then(
          async (response: any) =>
            response.items.sort((a: Entry, b: Entry) => a.fields.order - b.fields.order) as Entry[]
        )
        .then(setHelpCenterContent)
        .catch((err: any) => {
          setError(err.message)
          setIsLoadingContent(false)
        })
        .finally(() => {
          setIsLoadingContent(false)
        })
    }
  }, [helpCenterContent, setHelpCenterContent, t, language])

  useEffect(() => {
    if (!lastRowTabContent.length) {
      setIsLoadingLastRow(true)
      client
        .getEntries({ content_type: HELP_CENTER_RESOURCES_CONTENT_TYPE, locale: language })
        .then((response: any) => {
          setLastRowTabContent(response.items)
        })
        .catch(e => {
          console.error(e)
          setIsErrorLastRow(true)
          setIsLoadingLastRow(false)
        })
        .finally(() => {
          setIsLoadingLastRow(false)
        })
    }
  }, [lastRowTabContent, t, language])

  // Route to 404 if tabId is not found
  useEffect(() => {
    if (
      helpCenterContent.length &&
      !helpCenterContent.find(elem => elem.fields.id === tabId) &&
      tabId !== 'other'
    ) {
      // TODO: This shouldn't be routing to /notfound, but rendering the
      //       component that handles the not found pages. Navigating to
      //       a not found page is bad UX.
      navigate('/notfound')
    }
  }, [tabId])

  return (
    <BodyStack>
      <Stack>
        {isMobile && (
          <RootButton
            leftSection={<IconFilter size={20} />}
            className={classes.mobileCenterMenu}
            onClick={drawerOpened ? close : open}
          >
            {t('components.helpCenter.buttons.filterByTopic', 'Topic')}
            {/* {helpCenterContent.find(elem => elem.fields.id === activeTab)?.fields.title} */}
          </RootButton>
        )}
      </Stack>
      <Stack style={{ flexDirection: 'row', gap: '2px' }}>
        <Tabs
          className={classes.tabs}
          defaultValue={tabId}
          value={tabId}
          onChange={changeActiveTab}
          variant="outline"
          orientation="vertical"
        >
          {isMobile ? (
            <>
              <Drawer.Root
                opened={drawerOpened}
                onClose={close}
                size="xs"
                position="left"
                className={classes.drawer}
                lockScroll={false}
              >
                <Drawer.Overlay />

                <Drawer.Content>
                  <Drawer.Header>
                    <Drawer.CloseButton />
                  </Drawer.Header>
                  <Drawer.Body>
                    <Tabs.List className={classes.tabList}>
                      {helpCenterContent.map((item, index) => {
                        const { id } = helpCenterContent[index].fields
                        const { title } = helpCenterContent[index].fields
                        return (
                          <Tabs.Tab
                            style={{
                              color: tabId === id ? theme.colors.l1Primary[0] : '',
                            }}
                            leftSection={<ListIcon visible={tabId === id} />}
                            className={classes.tabHeading}
                            key={getKeyValue(item, index)}
                            onClick={close}
                            value={id}
                          >
                            {title}
                          </Tabs.Tab>
                        )
                      })}
                      <Tabs.Tab
                        style={{
                          color: tabId === 'other' ? theme.colors.l1Primary[0] : '',
                        }}
                        leftSection={<ListIcon visible={tabId === 'other'} />}
                        className={classes.tabHeading}
                        value="other"
                        onClick={close}
                      >
                        {t('components.helpCenter.otherTab.title', 'Additional Resources')}
                      </Tabs.Tab>
                    </Tabs.List>
                  </Drawer.Body>
                </Drawer.Content>
              </Drawer.Root>
            </>
          ) : (
            <Tabs.List className={classes.tabList}>
              {helpCenterContent.map((item, index) => {
                const { id } = helpCenterContent[index].fields
                const { title } = helpCenterContent[index].fields
                return (
                  <Tabs.Tab
                    style={{
                      color: tabId === id ? theme.colors.l1Primary[0] : '',
                    }}
                    leftSection={<ListIcon visible={tabId === id} />}
                    className={classes.tabHeading}
                    key={getKeyValue(item, index)}
                    value={id}
                  >
                    {title}
                  </Tabs.Tab>
                )
              })}
              <Tabs.Tab
                style={{
                  color: tabId === 'other' ? theme.colors.l1Primary[0] : '',
                }}
                leftSection={<ListIcon visible={tabId === 'other'} />}
                className={classes.tabHeading}
                value="other"
              >
                {t('components.helpCenter.otherTab.title')}
              </Tabs.Tab>
            </Tabs.List>
          )}

          {isLoadingContent && <Loading />}
          {error && <ErrorMessage message={errorMessages.cmsConnection} details={error} />}
          {!isLoadingContent &&
            !error &&
            helpCenterContent.map((item, index) => {
              const { id, title, body } = helpCenterContent[index].fields
              return (
                body && (
                  <Tabs.Panel
                    className={classes.tabPanel}
                    key={getKeyValue(item, index)}
                    value={id}
                  >
                    <Stack style={{ color: theme.colors.title[0] }}>
                      <Title order={3}>{title}</Title>
                      <ParseRichText jsonData={body} />
                    </Stack>
                  </Tabs.Panel>
                )
              )
            })}
          <Tabs.Panel className={classes.tabPanel} key={helpCenterContent.length} value="other">
            <Stack style={{ color: theme.colors.title[0] }}>
              <Title order={3}>
                {t(
                  'components.helpCenter.otherTab.helpFinding',
                  'Cannot Find What You are Looking For?'
                )}
              </Title>
              <Grid>
                {isLoadingLastRow && <Loading />}
                {isErrorLastRow && <ErrorMessage message={errorMessages.cmsConnection} />}
                {!isLoadingLastRow &&
                  !isErrorLastRow &&
                  lastRowTabContent.map((item, index) => {
                    const { id, title, button, link, description } = lastRowTabContent[index].fields
                    const image = lastRowTabContent[index].fields.image.fields.file.url
                    return (
                      <Grid.Col
                        key={getKeyValue(item, index)}
                        span={{
                          base: (12 / lastRowTabContent.length) * 4,
                          xs: (12 / lastRowTabContent.length) * 4,
                          md: (12 / lastRowTabContent.length) * 2,
                          lg: 12 / lastRowTabContent.length,
                        }}
                      >
                        <RowItemsTab
                          key={id}
                          image={image}
                          title={title}
                          description={<ParseRichText jsonData={description} />}
                          button={button}
                          link={link}
                        />
                      </Grid.Col>
                    )
                  })}
              </Grid>
              <Title order={3} style={{ marginTop: '16px' }}>
                {t('components.helpCenter.otherTab.submitFeedback', 'Submit Feedback')}
              </Title>
              <Text className={classes.paragraph}>
                <Trans i18nKey="components.helpCenter.otherTab.feedbackParagraph">
                  Found a bug? Have feedback on a page or functionality? Want to request a new
                  feature for the LAMINA1 Hub? We value your feedback. Please complete this survey
                  to share your thoughts with us.
                </Trans>
              </Text>
              <RootButton
                secondary
                onClick={() =>
                  window.open(
                    'https://blocksurvey.io/lamina1-hub-feedback-D.KIfzAfR9.0KNKCBAMGGQ-',
                    '_blank'
                  )
                }
              >
                {t('components.helpCenter.otherTab.buttonFeedback', 'Feedback Survey')}
              </RootButton>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </BodyStack>
  )
}
Component.displayName = 'HelpCenter'

// eslint-disable-next-line import/prefer-default-export
export { Component }
